import { Product } from "@/api/product-api";
import { useProductPrice } from "@/hooks/useProductPrice";
import { useCartWithProducts } from "@/store/useCartWithProducts";
import { useLocale } from "@/store/locale-store";
import { formatMoney } from "@/utils/CommonUtils";
import { useTrans } from "@/utils/useTrans";
import { GePrice } from "@/v2-components/GePrice";
import { useGeDisplayMarked } from "@/contexts/cart-ge-eligibility";
import { Badge } from "@/v2-components/ui/badge";
import { localeDependentProps } from "@/libs/locale/locale-ready";
import { geTextProps } from "@/libs/global-e/marker";
import {
  DiscontinuedLabel,
  isDiscontinued,
} from "@/components/tactsuit/PriceDisplay";

/**
 * 구매 레일 카드의 가격 행 — `Save $N` 배지 + 판매가 + 정가(취소선).
 *
 * 피그마 ④ Upgrade(28715:139579)와 ⑦ Add Badges(I28715:139636;21876:52934)가
 * **같은 구성**이라 한 곳으로 모았다. 예전엔 ④ 는 자체 조립, ⑦·⑧ 은
 * `TactSuitCard.PriceDetail` 을 써서 배지 문구가 갈렸다(`Save $N` vs `N% off`).
 *
 * 수치: 행 gap 4, 판매가 16/22 w600 ls.5 coolgray-90,
 *       정가 16/22 w400 ls.5 coolgray-50 취소선,
 *       배지는 `Badge variant=sale size=sm`(magenta-10/70, 12/16 w600, h20 py2 px6)
 *       — 피그마와 이미 일치하고 tracking 만 덧댄다.
 *
 * ⚠️ GE: "Save $X" 는 번역 문장에 금액이 끼워진 형태라 `GePrice`(=span)로 감쌀 수
 * 없다. 마커가 안 붙는 경우(KR/부적격/Fixed)에만 `data-gem-ignore` 로 GE 변환을
 * 차단해 아래 GePrice 두 개와 같은 규칙을 따르게 한다
 * (docs/global-e-storefront.md §4.11).
 */
export function RailCardPrice({ product }: { product: Product }) {
  const { t } = useTrans(["shop"]);
  const { countryCode } = useLocale();
  const { getProductPrice } = useCartWithProducts();
  const price = getProductPrice(product);
  const { displayOriginalPrice, displayDiscountAmount, displaySalePrice } =
    useProductPrice(price);
  const geMarked = useGeDisplayMarked(countryCode);

  // 단종 제품은 가격 대신 배지 — `PriceDisplay`·`TactSuitCard` 등 다른 가격 표시
  // 표면과 **같은 규칙**이다. 여기만 빠져 있으면 같은 제품이 카드 위치에 따라
  // 가격이 보였다 안 보였다 한다.
  // (현재 단종 목록은 Tactosy for Feet 하나뿐이고 이 레일 카드에 뜨는지는 실제
  //  packageDeals 데이터에 달렸다 — 확인 못 해 방어적으로 맞춰둔다)
  if (isDiscontinued(product.slug)) return <DiscontinuedLabel />;

  if (!price) return null;

  // ⚠️ 배지가 그려질 때만 만든다 — `formatMoney` 는 호출마다 `Intl.NumberFormat`
  // 을 새로 만들고(CommonUtils), 이 컴포넌트는 카드마다 마운트된다.
  // 할인 없는 액세서리 목록에서는 전부 버려지는 비용이었다.
  const savedAmount = displayDiscountAmount
    ? formatMoney({
        amount: displayDiscountAmount,
        currency: price.currency,
        countryCode,
      })
    : "";

  return (
    <div className="flex flex-wrap items-center gap-1">
      {!!displayDiscountAmount && (
        <Badge
          // ★`key` = 금액 — 금액이 바뀌면 배지를 새로 만든다(GePrice 의 `key={text}` 와 같은 이유, #6047). GE 가 변환하며
          //   남긴 상태 위에 글자만 원래 금액(`Save $N`)으로 바뀌면 재변환 전까지 보이거나, GE 감시 설정에 따라 다시
          //   변환되지 않을 수 있다 — 새로 붙은 요소는 GE 가 다시 처리한다.
          key={savedAmount}
          variant={"sale"}
          size={"sm"}
          className="tracking-[0.1px]"
          {...geTextProps(geMarked)}
          {...localeDependentProps}
        >
          {/* ⚠️ ns 를 지정하지 않는다 — 이 키는 **shop.json 에만** 있다(실측).
              `ns: "gnb"` 로 두면 어떤 gnb.json 에도 없어서 7개 locale 전부
              defaultValue(영어)로 조용히 떨어진다. */}
          {t("Save {{amount}}", {
            amount: savedAmount,
            defaultValue: `Save ${savedAmount}`,
          })}
        </Badge>
      )}
      <GePrice
        className="text-lg font-600 tracking-[0.5px] text-coolgray-90"
        amount={displaySalePrice}
        currency={price.currency}
        countryCode={countryCode}
      />
      {!!displayDiscountAmount && (
        <GePrice
          className="text-lg tracking-[0.5px] text-coolgray-50 line-through"
          amount={displayOriginalPrice}
          currency={price.currency}
          countryCode={countryCode}
        />
      )}
    </div>
  );
}
